import type { AnalyticsHealthFlag, AnalyticsHealthFlagType } from '../types/analytics.types';

/**
 * Flag types with a title under `detail.health.summary.flagTypes.<type>`. Anything
 * outside this list still renders, under the generic `fallback` title — `type` is an
 * open string in the analytics OpenAPI contract.
 */
const TITLED_FLAG_TYPES: ReadonlySet<AnalyticsHealthFlagType> = new Set([
  'outlier_rate',
  'out_of_stock',
  'traffic_split_drift',
  'arm_imbalance',
  'stale_source',
  'source_missing',
  'source_drop',
  'baseline_coverage',
  'all_sources_stale',
  'pre_period_imbalance',
  'provisional_recent_conversions',
]);

export interface HealthFlagTitle {
  key: string;
  /** Interpolation values; titles name the source / arm so sibling cards differ. */
  params: { source: string; arm: string };
}

function payloadString(flag: AnalyticsHealthFlag, field: string): string | null {
  const value = flag.payload?.[field];
  return typeof value === 'string' && value.trim() !== '' ? value : null;
}

/** i18n key + params for a flag card's title. */
export function healthFlagTitle(flag: AnalyticsHealthFlag): HealthFlagTitle {
  const known = TITLED_FLAG_TYPES.has(flag.type as AnalyticsHealthFlagType);
  return {
    key: `detail.health.summary.flagTypes.${known ? flag.type : 'fallback'}`,
    params: {
      source: payloadString(flag, 'source_label') ?? payloadString(flag, 'source') ?? '',
      arm: payloadString(flag, 'arm') ?? '',
    },
  };
}

/**
 * The backend's plain-English explanation (`payload.message`), or null when the flag
 * carries none — the caller then falls back to a structured payload summary.
 */
export function healthFlagMessage(flag: AnalyticsHealthFlag): string | null {
  return payloadString(flag, 'message');
}
